import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '@/components/LanguageContext'
import { buildLocalizedPath, stripLanguagePrefix, type Language } from '@/lib/i18n'
import { HREFLANGS, HTML_LANGS, OG_LOCALES } from '@/lib/languages'
import { publishedLanguages } from '@/lib/publication-matrix'

/**
 * Metadados do documento por idioma e por rota (Tarefa 10 / decisão D4).
 *
 * O que este hook ENTREGA de verdade, em runtime:
 * - `document.title` e `<meta name="description">` traduzidos;
 * - `document.documentElement.lang` (é ele que liga a tipografia CJK do
 *   `index.css` — ver `html[lang="ja"]`);
 * - Open Graph / Twitter Card por idioma;
 * - `<link rel="canonical">` e `<link rel="alternate" hreflang>` para as
 *   combinações (rota, idioma) REALMENTE PUBLICADAS, sempre com URL ABSOLUTA.
 *   Quem decide o que está publicado é `@/lib/publication-matrix` — fonte
 *   única, espelho do `vercel.json`.
 * - limpeza no unmount: sair da árvore pública (`/login`, `/app/*`) devolve
 *   `<head>` e `<html lang>` ao estado do shell estático, em vez de deixar
 *   canonical/hreflang/metas da última página pública pendurados.
 *
 * O que este hook NÃO entrega (honestidade da D4 — vale repetir aqui, junto do
 * código, e não só no plano): esta é uma SPA sem SSR. Tudo acima é escrito
 * DEPOIS que o JS executa. O Googlebot renderiza JS e tende a ler isso na
 * indexação renderizada, mas não há garantia de snippet por idioma, e os
 * scrapers de Open Graph que NÃO executam JS (WhatsApp, X, LinkedIn, Slack)
 * leem o HTML servido, não o que este hook escreve.
 *
 * O nível de IDIOMA desse buraco foi fechado na Tarefa 11: o build emite
 * `dist/{ja,en,es}.html` com `<html lang>`, title, OG e hreflang do idioma
 * (`scripts/postbuild-i18n-shells.mjs`), e o `vercel.json` serve cada prefixo a
 * partir do shell certo — compartilhar `/ja` mostra o card japonês.
 *
 * O nível de PÁGINA continua aberto e declarado: o card de `/ja/forum` é o card
 * japonês do SITE, não o do fórum japonês. Fechar isso exige prerender/SSR por
 * rota (PLANO-I18N §8). Nada aqui substitui isso.
 */

/**
 * `<html lang>`, `hreflang` e `og:locale` por idioma vêm do registro
 * (`@/lib/languages`) — fonte única. Acrescentar um idioma lá basta: nenhum
 * mapa precisa ser atualizado aqui.
 */
const HTML_LANG = HTML_LANGS
const HREFLANG = HREFLANGS
const OG_LOCALE = OG_LOCALES

const DEFAULT_SITE_ORIGIN = 'https://agrobioconnect.com'

/**
 * Valida `VITE_SITE_URL` e devolve a origem absoluta do site.
 *
 * `hreflang`, `canonical` e `og:url` exigem URL absoluta — um valor malformado
 * (`agrobioconnect.com`, `/`, `javascript:...`) produziria silenciosamente
 * metadados inválidos em produção. Só http/https passam; qualquer outra coisa
 * cai no default e avisa em desenvolvimento.
 */
export function resolveSiteOrigin(raw: string | undefined): string {
  if (!raw) return DEFAULT_SITE_ORIGIN
  try {
    const url = new URL(raw)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error(`protocolo não suportado: ${url.protocol}`)
    }
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '')
  } catch {
    if (import.meta.env?.DEV) {
      console.warn(
        `[useDocumentMeta] VITE_SITE_URL inválida (${raw}) — usando ${DEFAULT_SITE_ORIGIN}. ` +
          'Informe uma URL absoluta http(s), ex.: https://agrobioconnect.com',
      )
    }
    return DEFAULT_SITE_ORIGIN
  }
}

/** Origem absoluta do site, configurável por ambiente (preview não anuncia produção). */
export const SITE_ORIGIN: string = resolveSiteOrigin(
  import.meta.env?.VITE_SITE_URL as string | undefined,
)

/**
 * Primeiro segmento da rota (já sem prefixo de idioma) → família de chaves
 * `meta.*`. `/forum/:id` compartilha a meta de `/forum`: o título do tópico é
 * conteúdo do usuário, não string traduzida.
 */
const PAGE_BY_SEGMENT: Record<string, string> = {
  '': 'landing',
  inicio: 'inicio',
  diretorio: 'diretorio',
  mapa: 'mapa',
  forum: 'forum',
  inovacao: 'inovacao',
  cadastrar: 'cadastrar',
  conexoes: 'conexoes',
  contato: 'contato',
}

/** Função pura — testável sem DOM. */
export function metaPageKey(barePathname: string): string {
  const segment = (barePathname || '/').split('/').filter(Boolean)[0] ?? ''
  return PAGE_BY_SEGMENT[segment] ?? 'default'
}

/** URL absoluta da rota atual no idioma pedido. */
export function absoluteUrl(barePathname: string, lang: Language): string {
  const path = buildLocalizedPath(barePathname, lang)
  return `${SITE_ORIGIN}${path === '/' ? '/' : path}`
}

/**
 * Alternativas `hreflang` de uma rota — SOMENTE as publicadas.
 *
 * Rota represada no `coming-soon.html` (ex.: `/diretorio` em qualquer idioma)
 * devolve lista vazia: nenhum `hreflang` é emitido. Anunciar `/ja/diretorio`
 * como equivalente japonês do diretório enquanto o acesso direto entrega a
 * página de "em breve" seria declarar equivalência falsa ao buscador.
 *
 * `x-default` só entra quando o PT daquela rota está publicado — ele é o
 * destino de quem chega sem idioma declarado.
 */
export function hreflangAlternates(
  barePathname: string,
): { hreflang: string; href: string }[] {
  const languages = publishedLanguages(barePathname)
  if (languages.length === 0) return []
  const alternates: { hreflang: string; href: string }[] = languages.map((lang) => ({
    hreflang: HREFLANG[lang] as string,
    href: absoluteUrl(barePathname, lang),
  }))
  if (languages.includes('pt')) {
    alternates.push({ hreflang: 'x-default', href: absoluteUrl(barePathname, 'pt') })
  }
  return alternates
}

/** Desfaz uma escrita no `<head>` — remove o que criamos, restaura o que já existia. */
type Restore = () => void

function upsertMeta(attr: 'name' | 'property', key: string, content: string): Restore {
  const selector = `meta[${attr}="${key}"]`
  const existing = document.head.querySelector<HTMLMetaElement>(selector)
  if (existing) {
    const previous = existing.getAttribute('content')
    existing.setAttribute('content', content)
    return () => {
      if (previous === null) existing.removeAttribute('content')
      else existing.setAttribute('content', previous)
    }
  }
  const el = document.createElement('meta')
  el.setAttribute(attr, key)
  el.setAttribute('content', content)
  document.head.appendChild(el)
  return () => el.remove()
}

function upsertCanonical(href: string): Restore {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (existing) {
    const previous = existing.getAttribute('href')
    existing.setAttribute('href', href)
    return () => {
      if (previous === null) existing.removeAttribute('href')
      else existing.setAttribute('href', previous)
    }
  }
  const el = document.createElement('link')
  el.setAttribute('rel', 'canonical')
  el.setAttribute('href', href)
  document.head.appendChild(el)
  return () => el.remove()
}

/**
 * Reescreve APENAS os `<link rel="alternate" hreflang>` marcados com
 * `data-i18n-alternate` — os que ESTE hook criou (`="true"`) e os que o shell
 * estático por idioma emitiu (`="shell"`, ver `scripts/postbuild-i18n-shells.mjs`).
 *
 * Os dois são nossos, e a versão do hook é a melhor: o shell só sabe o IDIOMA
 * (é servido antes de qualquer rota resolver), enquanto aqui sabemos a ROTA e
 * consultamos a matriz de publicação. Deixar ambos no `<head>` anunciaria duas
 * verdades diferentes para o mesmo par (idioma, página). Um `hreflang` de
 * terceiros, sem a marca, continua intocado.
 */
function replaceHreflangLinks(alternates: { hreflang: string; href: string }[]): Restore {
  document.head
    .querySelectorAll('link[data-i18n-alternate]')
    .forEach((el) => el.remove())
  const created: HTMLLinkElement[] = []
  for (const { hreflang, href } of alternates) {
    const el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', hreflang)
    el.setAttribute('href', href)
    el.setAttribute('data-i18n-alternate', 'true')
    document.head.appendChild(el)
    created.push(el)
  }
  return () => created.forEach((el) => el.remove())
}

export function useDocumentMeta(): void {
  const { t, language } = useLanguage()
  const { pathname } = useLocation()
  const original = useRef<{ title: string; lang: string } | null>(null)

  // Efeito de MONTAGEM (deps vazias): guarda o estado do shell estático e o
  // devolve ao sair da árvore pública. Separado do efeito por rota porque
  // aquele roda a cada navegação — o "original" precisa ser capturado uma vez.
  useEffect(() => {
    if (typeof document === 'undefined') return
    original.current = {
      title: document.title,
      lang: document.documentElement.getAttribute('lang') ?? '',
    }
    return () => {
      const snapshot = original.current
      if (!snapshot) return
      document.title = snapshot.title
      if (snapshot.lang) document.documentElement.setAttribute('lang', snapshot.lang)
      else document.documentElement.removeAttribute('lang')
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const bare = stripLanguagePrefix(pathname)
    const page = metaPageKey(bare)

    // `translate` devolve a própria chave quando ela não existe em nenhum
    // dicionário; nesse caso caímos na meta padrão em vez de imprimir a chave.
    const resolve = (key: string, fallbackKey: string): string => {
      const value = t(key)
      return value && value !== key ? value : t(fallbackKey)
    }

    const title = resolve(`meta.${page}.title`, 'meta.default.title')
    const description = resolve(`meta.${page}.description`, 'meta.default.description')
    const siteName = t('meta.siteName')
    const canonical = absoluteUrl(bare, language)

    document.documentElement.lang = HTML_LANG[language] ?? HTML_LANG.pt
    document.title = title

    const restores: Restore[] = [
      upsertMeta('name', 'description', description),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:site_name', siteName),
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:url', canonical),
      upsertMeta('property', 'og:locale', OG_LOCALE[language] ?? OG_LOCALE.pt),
      upsertMeta('property', 'og:image', `${SITE_ORIGIN}/logo.jpg`),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertMeta('name', 'twitter:image', `${SITE_ORIGIN}/logo.jpg`),
      upsertCanonical(canonical),
      replaceHreflangLinks(hreflangAlternates(bare)),
    ]

    return () => {
      // Ordem inversa: o último a escrever é o primeiro a desfazer.
      for (const restore of restores.reverse()) restore()
    }
  }, [t, language, pathname])
}
